import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Grid, Box, Collapse, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import vehiclesService from "services/vehiclesService";
import "mapbox-gl/dist/mapbox-gl.css";
import chevronIcon from "assets/images/chevron-blue-24.png";
import stopIcon from "assets/images/stop-sign-no-text-48.png";
import markerIcon from "assets/images/marker-48.png";
import locationIcon from "assets/images/red-map-pin-60.png";
import type { FeatureCollection } from "geojson";
import { VehicleHistorySearch, VehicleActivitySummary } from "map/model";
import { EnsureTimeZoneFromAbbreviation, GetTimeZoneFromAbbreviation } from "common/Constants";
import moment from "moment";
import { debounce, map } from "lodash";
import axios from "axios";
import { bbox, lineString } from "@turf/turf";
import mapboxgl from "mapbox-gl";
import VehicleActivityCards from "./VehicleActivityCards";
import { useSearchParams } from "react-router-dom";
import VehicleHistoryFilters from "./VehicleHistoryFilters";
import ActivitySummary from "./ActivitySummary";
import configProvider from "config/configProvider";
import { webglUnavailableMessage } from "../webglSupport";
import {
  vehicleTripData,
  getVehicleTripData,
  clearVehicleTripData,
  getTimeline,
  tripTimeline,
  timeZone,
  setTimeZone,
  mapStyleReloadedCount,
  setMapStyleReloadedCount,
  isTimelineLoading,
  isVehicleTripLoading,
} from "store/vehicleTripSlice";
import { getTrip, tripData } from "store/tripSlice";
import { useDispatch, useSelector } from "react-redux";
interface IVehicleHistoryProps {
  hideFilters?: boolean;
  tripNumber?: string;
  isHidden?: boolean;
  sourceIdPrefix?: string;
  selectedEventId?: number;
  onEventSelected?: (eventId: number | null) => void;
  onMapDataReady?: (ready: boolean) => void;
  // When true, the driver-entered event markers are not drawn on the map (used by the GPS
  // Reconstruction tab, which is meant to show the GPS track on its own, not the driver's pins).
  hideDriverEvents?: boolean;
  filterOnly?: boolean; // When true, date changes filter existing data instead of re-fetching
  rightPanelContent?: React.ReactNode; // When provided, replaces VehicleActivityCards in the right panel
  // When set (from a right-panel row click, e.g. the GPS Reconstruction tab), pan/zoom the map to
  // this point. The changing nonce lets the same coordinates re-trigger a fly-to on repeat clicks.
  focusPoint?: { latitude: number; longitude: number; nonce?: number };
  // Numbered markers to plot on the map (the GPS Reconstruction tab passes its cards' points here so
  // the badge on each card lines up with the numbered marker on the map). When provided, the raw GPS
  // stop markers are suppressed so there's a single, matching set of numbers.
  numberedMarkers?: Array<{ latitude: number; longitude: number; label: number | string }>;
  // Event editing props - passed through to VehicleActivityCards
  editable?: boolean;
  eventTypes?: any[];
  waitReasons?: any[];
  pointsOfService?: any[];
  onEventSave?: (event: any) => void;
  onEventRemove?: (event: any) => void;
  onEventsReorder?: (events: any[]) => void;
  // When provided, used in place of Redux tripTimeline for the activity-cards build.
  // Lets the parent stage edits/reorders locally and have them reflected immediately
  // in the right panel without a server round trip.
  tripEventsOverride?: any[];
}

mapboxgl.accessToken = configProvider.MapboxApiKey;

/** How many times a lost WebGL context will be rebuilt before we show the unavailable message. */
const MAX_MAP_REBUILDS = 3;
/** A map that has not finished loading by now is not going to on its own. */
const MAP_LOAD_WATCHDOG_MS = 8000;

const VehicleHistory = (props: IVehicleHistoryProps) => {
  const dispatch = useDispatch();
  const sourcePrefix = props.sourceIdPrefix || 'default';
  
  const cleanupMapSources = () => {
    if (!mapRef.current) return;
    // getLayer/getSource reach into map.style, which is already gone once the map has been
    // removed or while a style is being swapped. Calling them then throws
    // "Cannot read properties of undefined (reading 'getOwnLayer')" -- and because this runs from
    // an effect cleanup, that exception took the whole component down through the error boundary.
    if (!mapRef.current.getStyle || !mapRef.current.getStyle()) return;
    
    const sourcesToRemove = [
      `${sourcePrefix}-stops`,
      `${sourcePrefix}-trip-events`,
      `${sourcePrefix}-headings`,
      `${sourcePrefix}-vehicles`,
      `${sourcePrefix}-client-points`,
      `${sourcePrefix}-recon-markers`
    ];

    const layersToRemove = [
      `${sourcePrefix}-stops-clusters`,
      `${sourcePrefix}-stops-layer`,
      `${sourcePrefix}-trip-events-layer`,
      `${sourcePrefix}-headings-layer`,
      `${sourcePrefix}-lines-layer`,
      `${sourcePrefix}-points-layer`,
      `${sourcePrefix}-recon-markers-circle`,
      `${sourcePrefix}-recon-markers-text`
    ];
    
    layersToRemove.forEach(layerId => {
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }
    });
    
    sourcesToRemove.forEach(sourceId => {
      if (mapRef.current.getSource(sourceId)) {
        mapRef.current.removeSource(sourceId);
      }
    });
  };
  const [mapCamera, setMapCamera] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 3.5,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [lng, setLng] = useState(-100);
  const [lat, setLat] = useState(40);

  const [search, setSearch] = useState<VehicleHistorySearch>({
    unitNumber: undefined,
    startDate: moment().subtract(1, "d"),
    endDate: moment(),
    timezone: new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2],
    tripNumber: props.tripNumber,
  });
  const [mapLoaded, setMapLoaded] = useState(false);
  const _allData = useSelector(vehicleTripData);
  const [activity, setActivity] = useState(undefined);
  const [summary, setSummary] = useState<VehicleActivitySummary>(undefined);
  const [selectedRow, setSelectedRow] = useState(undefined);
  // Set of activity-card row ids the user has highlighted. Toggle-on-click; multiple
  // can be highlighted at once. Highlighted segments get an inverted-color overlay
  // (navy line + grey chevrons) drawn on top of the default grey/blue layers.
  const [highlightedRowIds, setHighlightedRowIds] = useState<Set<any>>(new Set());

  // Play-along animation: little van drives the route from start to end.
  // Mostly so dispatchers can show off the timeline. Beep beep.
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Playback speed multiplier. 1× = base duration; 0.5× drives at half speed.
  const [playSpeed, setPlaySpeed] = useState(1);
  const playSpeedRef = useRef(1);
  const playMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const playRafRef = useRef<number | null>(null);
  // Progress is accumulated per-frame (0→1) instead of derived from an absolute
  // start time, so pausing just stops advancing and resuming picks up where it
  // left off — and changing playSpeed mid-drive takes effect on the next frame.
  const playProgressRef = useRef<number>(0);
  const playLastFrameRef = useRef<number>(0);
  // Marker DOM refs kept around so resume can keep driving the same van.
  const playInnerRef = useRef<HTMLElement | null>(null);
  const playPathRef = useRef<Array<{ lng: number; lat: number; heading?: number }>>([]);

  // Keep the speed ref in sync so the rAF loop (which closes over refs, not state)
  // reads the latest multiplier without restarting.
  useEffect(() => { playSpeedRef.current = playSpeed; }, [playSpeed]);
  const [layerToggle, setLayerToggle] = useState({
    stops: true,
    headings: true,
    lines: true,
    headingsEnabled: true,
    linesEnabled: true,
    points: true,
    satellite: false
  });
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Bumped when a lost WebGL context forces the map to be rebuilt -- see the map lifetime effect.
  const [mapGeneration, setMapGeneration] = useState(0);
  const mapGenerationRef = useRef(0);
  const [webglError, setWebglError] = useState<string | null>(null);
  const urlTripNumber = new URLSearchParams(window.location.search).get("tripNumber");
  const tripDatesReady = useRef(!props.tripNumber && !urlTripNumber); // true for standalone, false for trip mode
  const tripStartDate = useRef<string | null>(null);
  const tripEndDate = useRef<string | null>(null);
  const summaryCancelRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const timezone = useSelector(timeZone);
  const trip = useSelector(tripData);
  const [orderedTimeline, setOrderedTimeline] = useState(undefined);
  const _tripTimeline = useSelector(tripTimeline);
  const timelineLoading = useSelector(isTimelineLoading);
  const vehicleTripLoading = useSelector(isVehicleTripLoading);
  const [orderedActivity, setOrderedActivity] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(undefined);
  const [orderedStops, setOrderedStops] = useState(undefined);
  const [clientPoints, setClientPoints] = useState(undefined);
  const clientPointsRef = useRef(clientPoints);
  const [activityLoading, setActivityLoading] = useState(true);
  const [dataInitialized, setDataInitialized] = useState(false);
  /**
   * Per-instance style-reload counter. Deliberately NOT the Redux one.
   *
   * A trip page mounts four of these maps at once -- Map, Closer, GPS Reconstruction and the
   * autoclose preview all live in hidden tab panels, so they are all mounted, not just the tab on
   * screen. Sharing one counter in Redux meant switching ONE map to satellite bumped a value every
   * instance was watching: all four tore down and rebuilt their layers, and each reset the shared
   * counter to zero as it finished, so the map that actually changed style had its own restore
   * cancelled out from under it by its siblings. That is why this worked on the standalone
   * Vehicle History screen, which mounts exactly one, and failed on a trip.
   */
  const [_mapStyleReloadedCount, setStyleReloadCount] = useState(0);
  const [clientPointsCache, setClientPointsCache] = useState(new Map());
  const [mapWidthPercent, setMapWidthPercent] = useState(50);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(!!props.tripNumber);

  useEffect(() => {
    clientPointsRef.current = clientPoints;
  }, [clientPoints]);

  // Redraw the client-points layer whenever clientPoints state changes. Without this,
  // the layer only gets drawn when setLayers() happens to fire AFTER the async client
  // points fetch resolves — and most setLayers callers (activity/timeline effects) run
  // first with clientPoints still undefined, which makes buildPointsCollection early-
  // return and leave the layer empty. Net effect: points appeared or didn't based on
  // which fetch won the race. Keying redraw on clientPoints makes it deterministic.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !clientPoints) return;
    buildPointsCollection(clientPoints);
  }, [clientPoints, mapLoaded]);

  const onLoad = () => {
    if (!mapRef.current) return;
    loadImagesAndSources();
  }
  /**
   * Adds a map image and resolves once it is actually registered.
   *
   * These are loaded through Image.onload, so they land asynchronously. That never mattered while
   * mapbox diffed one style into the next and carried the images over -- but a full style reload
   * drops every image, and any symbol layer built before its icon exists silently draws nothing.
   * That is why a style switch used to come back with the route line but no van, no stops and no
   * points of interest: the line layer needs no icon, so it was the only one that survived.
   *
   * Resolves rather than rejects on error so one bad icon cannot strand the whole map.
   */
  const addMapImage = (
    name: string,
    src: string,
    width: number,
    height: number,
    options?: { sdf: boolean }
  ) =>
    new Promise<void>((resolve) => {
      const image = new Image();
      image.onload = () => {
        if (mapRef.current && !mapRef.current.hasImage(name)) {
          mapRef.current.addImage(name, image, options);
        }
        resolve();
      };
      image.onerror = () => resolve();
      image.width = width;
      image.height = height;
      image.src = src;
    });

  /** Resolves once every icon is registered, so callers can safely build symbol layers after. */
  const loadImagesAndSources = () => {
    const images = Promise.all([
      addMapImage("chevron", chevronIcon, 25, 25, { sdf: true }),
      addMapImage("stop", stopIcon, 32, 32),
      addMapImage("event-marker", markerIcon, 33, 32),
      addMapImage("location", locationIcon, 40, 40),
    ]).then(() => undefined);

    setMapLoaded(true);

    addLinesSource();
    addHeadingsSource();
    addPointsSource();
    addStopsSource();
    addEventsSource();
    addReconMarkersSource();
    addHighlightSources();
   setStyleReloadCount(0);

    return images;
  };

  const onStyleLoad = ()=>{
    setStyleReloadCount((c) => c + 1);
    // setStyle re-applies the incoming style's own projection, so the constructor's mercator does
    // not survive the switch to satellite. Re-assert it whenever a style finishes loading.
    mapRef.current?.setProjection({ name: "mercator" });
  }

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.on("mouseenter", `${sourcePrefix}-stops-layer`, onMouseEnterStops);
    mapRef.current.on("mouseleave", `${sourcePrefix}-stops-layer`, onMouseLeave);
    mapRef.current.on("click", `${sourcePrefix}-stops-layer`, onLayerClick);

    mapRef.current.on("mouseenter", `${sourcePrefix}-headings-layer`, onMouseEnterHeadings);
    mapRef.current.on("mouseleave", `${sourcePrefix}-headings-layer`, onMouseLeave);
    mapRef.current.on("click", `${sourcePrefix}-headings-layer`, onLayerClick);

    mapRef.current.on("mouseenter", `${sourcePrefix}-trip-events-layer`, onMouseEnterEvents);
    mapRef.current.on("mouseleave", `${sourcePrefix}-trip-events-layer`, onMouseLeave);
    mapRef.current.on("click", `${sourcePrefix}-trip-events-layer`, onLayerClick);

    mapRef.current.on("mouseenter", `${sourcePrefix}-points-layer`, onMouseEnterPoints);
    mapRef.current.on("mouseleave", `${sourcePrefix}-points-layer`, onMouseLeave);
    mapRef.current.on("click", `${sourcePrefix}-points-layer`, onLayerClick);

    const clusterLayerId = `${sourcePrefix}-stops-clusters`;

    const onClusterClick = (e) => {
      const features = mapRef.current.queryRenderedFeatures(e.point, { layers: [clusterLayerId] });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      (mapRef.current.getSource(`${sourcePrefix}-stops`) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        mapRef.current.easeTo({
          center: (features[0].geometry as GeoJSON.Point).coordinates as [number, number],
          zoom: zoom,
        });
      });
    };

    const onClusterMouseEnter = () => {
      mapRef.current.getCanvas().style.cursor = "pointer";
    };
    const onClusterMouseLeave = () => {
      mapRef.current.getCanvas().style.cursor = "";
    };

    mapRef.current.on("click", clusterLayerId, onClusterClick);
    mapRef.current.on("mouseenter", clusterLayerId, onClusterMouseEnter);
    mapRef.current.on("mouseleave", clusterLayerId, onClusterMouseLeave);

    return () => {
      if (mapRef.current) {
        mapRef.current.off("mouseenter", `${sourcePrefix}-stops-layer`, onMouseEnterStops);
        mapRef.current.off("mouseleave", `${sourcePrefix}-stops-layer`, onMouseLeave);
        mapRef.current.off("click", `${sourcePrefix}-stops-layer`, onLayerClick);

        mapRef.current.off("mouseenter", `${sourcePrefix}-headings-layer`, onMouseEnterHeadings);
        mapRef.current.off("mouseleave", `${sourcePrefix}-headings-layer`, onMouseLeave);
        mapRef.current.off("click", `${sourcePrefix}-headings-layer`, onLayerClick);

        mapRef.current.off("mouseenter", `${sourcePrefix}-trip-events-layer`, onMouseEnterEvents);
        mapRef.current.off("mouseleave", `${sourcePrefix}-trip-events-layer`, onMouseLeave);
        mapRef.current.off("click", `${sourcePrefix}-trip-events-layer`, onLayerClick);

        mapRef.current.off("mouseenter", `${sourcePrefix}-points-layer`, onMouseEnterPoints);
        mapRef.current.off("mouseleave", `${sourcePrefix}-points-layer`, onMouseLeave);
        mapRef.current.off("click", `${sourcePrefix}-points-layer`, onLayerClick);

        mapRef.current.off("click", clusterLayerId, onClusterClick);
        mapRef.current.off("mouseenter", clusterLayerId, onClusterMouseEnter);
        mapRef.current.off("mouseleave", clusterLayerId, onClusterMouseLeave);
      }
    };
  }, [mapRef.current, timezone]);

  const onMouseLeave = (e) => {
    mapRef.current.getCanvas().style.cursor = "";
    // Hover popup always removed on mouseleave. Pinned popups are independent.
    popup.remove();
  };

  // Click snapshots the hover popup's current content + position into a new
  // pinned popup (light, with an X). The hover popup is removed so the pinned
  // one takes its place visually. Multiple clicks accumulate independent pins.
  const onLayerClick = () => {
    const ll = popup.getLngLat();
    const content = popup.getElement()?.querySelector(".mapboxgl-popup-content");
    const html = content?.innerHTML;
    if (!ll || !html || !mapRef.current) return;
    const pinned = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      className: "vh-popup-dark vh-popup-pinned",
    });
    pinned.on("close", () => { pinnedPopups.current.delete(pinned); });
    pinned.setLngLat(ll).setHTML(html).addTo(mapRef.current);
    pinnedPopups.current.add(pinned);
    popup.remove();
  };

  const onMouseEnterStops = (e) => {
    mapRef.current.getCanvas().style.cursor = "pointer";

    // Copy coordinates array.
    const coordinates = e.features[0].geometry["coordinates"].slice();
    const duration = e.features[0].properties.duration;
    const occurence = e.features[0].properties.occurrenceDT
      ? moment
          .utc(e.features[0].properties.occurrenceDT + "Z")
          .tz(GetTimeZoneFromAbbreviation(timezone))
          .format("MM/DD/YYYY HH:mm")
      : "N/A";
    const lat = e.features[0].properties.latitude ?? "N/A";
    const long = e.features[0].properties.longitude ?? "N/A";
    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup
      .setLngLat(coordinates)
      .setHTML(
        `<div class="fw-bold ml-2">Duration: ${duration} min</div><div>Date: ${occurence}</div><div class="row">Lat: ${lat}</div><div class="row">Long: ${long}</div>`
      )
      .addTo(mapRef.current);
  };

  const onMouseEnterHeadings = (e) => {
    mapRef.current.getCanvas().style.cursor = "pointer";
    // Copy coordinates array.
    const coordinates = e.features[0].geometry["coordinates"].slice();
    const occurence = e.features[0].properties.occurrenceDT
      ? moment
          .utc(e.features[0].properties.occurrenceDT + "Z")
          .tz(GetTimeZoneFromAbbreviation(timezone))
          .format("MM/DD/YYYY HH:mm")
      : "N/A";
    const speed = e.features[0].properties.speedMPH;
    const lat = e.features[0].properties.latitude ?? "N/A";
    const long = e.features[0].properties.longitude ?? "N/A";

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup
      .setLngLat(coordinates)
      .setHTML(
        `<div class="row">Date: ${occurence}</div><div class="row">Speed: ${speed} MPH</div><div class="row">Lat: ${lat}</div><div class="row">Long: ${long}</div>`
      )
      .addTo(mapRef.current);
  };

  const onMouseEnterEvents = (e) => {
    mapRef.current.getCanvas().style.cursor = "pointer";
    // Copy coordinates array.
    const coordinates = e.features[0].geometry["coordinates"].slice();
    const occurence = e.features[0].properties.time
      ? moment.utc(e.features[0].properties.time).tz(GetTimeZoneFromAbbreviation(timezone)).format("MM/DD/YYYY HH:mm")
      : "N/A";
    const eventType = e.features[0].properties.type;
    const comments = e.features[0].properties.comments ?? "N/A";
    const lat = JSON.parse(e.features[0].properties.location)?.latitude ?? null;
    const long = JSON.parse(e.features[0].properties.location)?.longitude ?? null;

    // Find nearest Point of Service
    let nearestPosName = "";
    const points = clientPointsRef.current;
    if (lat != null && long != null && points?.length) {
      let minDist = Infinity;
      for (const point of points) {
        if (!point.latitude || !point.longitude) continue;
        const dist = Math.sqrt(Math.pow(point.latitude - lat, 2) + Math.pow(point.longitude - long, 2));
        if (dist < minDist) {
          minDist = dist;
          nearestPosName = point.pointName || point.name || "";
        }
      }
    }
    const posHtml = nearestPosName ? `<div class="row fw-bold">Nearest POS: ${nearestPosName}</div>` : "";

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup
      .setLngLat(coordinates)
      .setHTML(
        `<div class="row">Date: ${occurence}</div><div class="row">Type: ${eventType}</div><div class="row">Comments: ${comments}</div>${posHtml}<div class="row">Lat: ${lat ?? "N/A"}</div><div class="row">Long: ${long ?? "N/A"}</div>`
      )
      .addTo(mapRef.current);
  };

  const onMouseEnterPoints = (e) => {
    mapRef.current.getCanvas().style.cursor = "pointer";

    // Copy coordinates array.
    const coordinates = e.features[0].geometry["coordinates"].slice();
    const pointName = e.features[0].properties.pointName;

    const lat = e.features[0].properties.latitude ?? "N/A";
    const long = e.features[0].properties.longitude ?? "N/A";
    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup
      .setLngLat(coordinates)
      .setHTML(
        `<div class="fw-bold ml-2">Name: ${pointName} </div><div class="row">Lat: ${lat}</div><div class="row">Long: ${long}</div>`
      )
      .addTo(mapRef.current);
  };

  const addStopsSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-stops`;
    const layerId = `${sourcePrefix}-stops-layer`;
    
    const clusterLayerId = `${sourcePrefix}-stops-clusters`;


    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 40,
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;

    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "symbol",
        source: sourceId,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": "stop",
          "icon-allow-overlap": true,
          "text-field": "{index}",
          "text-size": 13,
        },
        paint: {
          "text-color": "white",
        },
      });
    }

    if (!map.getLayer(clusterLayerId)) {
      map.addLayer({
        id: clusterLayerId,
        type: "circle",
        source: sourceId,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#f28cb1",
          "circle-radius": [
            "step",
            ["get", "point_count"],
            15,
            10, 20,
            50, 25,
            100, 30,
          ],
        },
      });
    }

  };

  const addEventsSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-trip-events`;
    const layerId = `${sourcePrefix}-trip-events-layer`;
    
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;
    
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "symbol",
        source: sourceId,
        layout: {
          "icon-image": "event-marker",
          "icon-allow-overlap": true,
          "text-field": "{index}",
          "text-size": 13,
        },
        paint: {
          "text-color": "white",
        },
      });
    }
  };

  // Numbered reconstruction markers: a blue disc with the card's number. Non-clustered so every
  // number stays visible and lines up 1:1 with the right-panel cards on the GPS Reconstruction tab.
  const addReconMarkersSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-recon-markers`;
    const circleLayerId = `${sourcePrefix}-recon-markers-circle`;
    const textLayerId = `${sourcePrefix}-recon-markers-text`;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;

    if (!map.getLayer(circleLayerId)) {
      map.addLayer({
        id: circleLayerId,
        type: "circle",
        source: sourceId,
        paint: {
          "circle-radius": 13,
          "circle-color": "#1565c0",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
    }

    if (!map.getLayer(textLayerId)) {
      map.addLayer({
        id: textLayerId,
        type: "symbol",
        source: sourceId,
        layout: {
          "text-field": ["get", "label"],
          "text-size": 13,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          // A thin dark halo keeps the number readable even if two discs partially overlap.
          "text-halo-color": "rgba(0,0,0,0.45)",
          "text-halo-width": 1,
        },
      });
    }
  };

  const addHeadingsSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-headings`;
    const layerId = `${sourcePrefix}-headings-layer`;
    
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;
    
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "symbol",
        source: sourceId,
        minzoom: 8,
        layout: {
          "icon-image": "chevron",
          "icon-allow-overlap": true,
          "icon-rotate": ["get", "heading"],
          // Source PNG is 25px; route line is 8px wide. Scale with zoom for visibility:
          // ~0.35 (~9px) when zoomed out, doubling to ~0.7 as you zoom in to street level.
          "icon-size": ["interpolate", ["linear"], ["zoom"], 12, 0.35, 15, 0.7],
        },
        paint: {"icon-color": "#0d1b6e"}
      });
    }
  };

  // Overlay sources/layers for highlighted segments. Default route stays grey/blue;
  // these draw the user's clicked segments in inverted colors (navy line + grey
  // chevrons) on top so multi-select is obvious without losing route context.
  const addHighlightSources = () => {
    let map = mapRef.current;
    const linesSourceId = `${sourcePrefix}-highlight-lines`;
    const linesLayerId = `${sourcePrefix}-highlight-lines-layer`;
    const headingsSourceId = `${sourcePrefix}-highlight-headings`;
    const headingsLayerId = `${sourcePrefix}-highlight-headings-layer`;
    const emptyFC = { type: "FeatureCollection", features: [] } as any;

    if (!map.getSource(linesSourceId)) {
      map.addSource(linesSourceId, { type: "geojson", data: emptyFC });
    }
    if (!map.getLayer(linesLayerId)) {
      map.addLayer({
        id: linesLayerId,
        type: "line",
        source: linesSourceId,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#1a237e", "line-width": 8 }, // navy
      });
    }

    if (!map.getSource(headingsSourceId)) {
      map.addSource(headingsSourceId, { type: "geojson", data: emptyFC });
    }
    if (!map.getLayer(headingsLayerId)) {
      map.addLayer({
        id: headingsLayerId,
        type: "symbol",
        source: headingsSourceId,
        minzoom: 8,
        layout: {
          "icon-image": "chevron",
          "icon-allow-overlap": true,
          "icon-rotate": ["get", "heading"],
          "icon-size": 0.3,
        },
        paint: { "icon-color": "#888888" }, // grey, inverted from default blue
      });
    }
  };

  const addLinesSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-vehicles`;
    const layerId = `${sourcePrefix}-lines-layer`;
    
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: [],
              },
            },
          ],
        },
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;

    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#888",
          "line-width": 8,
        },
      });
    }
  };

  const addPointsSource = () => {
    let map = mapRef.current;
    const sourceId = `${sourcePrefix}-client-points`;
    const layerId = `${sourcePrefix}-points-layer`;
    
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }

    let source = mapRef.current.getSource(sourceId);
    source["_options"]["dynamic"] = true;
    
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "symbol",
        source: sourceId,
        layout: {
          "icon-image": "location",
          "icon-allow-overlap": true,
          "text-field": "{pointName}",
          "text-size": 12,
          "text-offset": [0, 2],
        },
        paint: {
          "text-color": "black",
          "text-halo-color": "white",
          "text-halo-width": 2,
        },
      });
    }
  };

  // Hover popup: transient, dark. Recreated on every mouseenter, removed on
  // mouseleave. Has no X — it's a tooltip, not interactive.
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: "vh-popup-dark",
  });
  // Pinned popups: persistent, light. Each click creates a new one that stays
  // until the user clicks its X. Stored in a ref so multiple clicks accumulate.
  const pinnedPopups = useRef<Set<mapboxgl.Popup>>(new Set());

  // Separate effect for reading URL search parameters - runs when URL changes
  useEffect(() => {
    let timezone = EnsureTimeZoneFromAbbreviation(searchParams.get("timezone"));

    if (searchParams.get("tripNumber")) {
      // Trip number mode
      const tripNumber = searchParams.get("tripNumber");
      setSearch((prev) => ({ ...prev, tripNumber, timezone: timezone.toUpperCase() }));
      dispatch(getTrip(tripNumber));
    } else if (searchParams.get("unitNumber")) {
      // Unit number mode - only update if we have unitNumber
      const unitNumber = +searchParams.get("unitNumber");
      const startDate = searchParams.get("startDate") ? moment.utc(searchParams.get("startDate")) : moment().subtract(1, "d");
      const endDate = searchParams.get("endDate") ? moment.utc(searchParams.get("endDate")) : moment();

      setSearch((prev) => ({
        ...prev,
        unitNumber: unitNumber,
        startDate: startDate,
        endDate: endDate,
        timezone: timezone.toUpperCase(),
        tripNumber: undefined, // Clear trip number when searching by unit
      }));
    }

    dispatch(
      setTimeZone(
        timezone.toUpperCase() ?? new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2]
      )
    );
  }, [searchParams]); // Re-run when URL search params change

  // Map lifetime: creation and teardown in ONE effect.
  //
  // These used to be two effects sharing a [sourcePrefix] dependency -- one that constructed the
  // map and a second whose only job was to remove it. Nothing tied the two together, so whether a
  // map got torn down before the next one was built came down to the order React happened to run
  // them in. Under StrictMode's double-invoke and under Fast Refresh that ordering is not the one
  // you want, and the component ends up having constructed a second mapboxgl.Map against the same
  // container while the first is still attached. Two maps then share one canvas element and
  // neither ever finishes loading.
  //
  // Returning the cleanup from the same effect that creates the map makes that impossible: React
  // guarantees a setup's own cleanup runs before that setup runs again.
  useEffect(() => {
    const generation = mapGeneration;
    if (mapRef.current) return;
    if (!mapContainer.current) return;
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return;

    try {
      mapRef.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [lng, lat],
        zoom: 4,
        // Flat map, always. Mapbox GL v3 honours the projection a style declares, and the
        // satellite-streets styles declare "globe" -- which draws the map as a shaded sphere on a
        // near-black space background. Every screen here reads as a flat US map and the trip
        // overlays are positioned for one, so pin mercator rather than inherit.
        projection: { name: "mercator" },
        // Keep serving a map on weak/virtualised GPUs rather than refusing to create one. The
        // people who need this screen most are the ones most likely to be on such a machine.
        failIfMajorPerformanceCaveat: false,
      });
      mapGenerationRef.current = generation;
    } catch (e) {
      setWebglError(webglUnavailableMessage);
      return;
    }

    // WebGL context loss is why this map goes black.
    //
    // When the browser drops the canvas' GL context, mapbox does not recover and it does not
    // raise anything through map.on("error") -- the loss arrives as a DOM event on the canvas
    // instead. The map keeps its style but never initialises its sources again, so it stops
    // requesting tiles entirely and simply paints the style's background colour. On the satellite
    // styles that background is near-black, which is the black map the closers report; catch it
    // before any tiles were drawn and even the roads are missing. Nothing in the UI said a word,
    // because as far as the old code was concerned nothing had failed.
    //
    // Contexts get dropped for reasons outside our control -- GPU driver resets, the OS
    // reclaiming resources, a machine under memory pressure, remote sessions -- so the fix is to
    // notice and rebuild rather than to try to prevent it. preventDefault() asks the browser to
    // let the context be restored at all; bumping the generation tears this map down through the
    // effect's own cleanup and builds a fresh one.
    const canvas = mapRef.current.getCanvas();
    const handleContextLost = (ev: Event) => {
      ev.preventDefault();
      if (mapGenerationRef.current >= MAX_MAP_REBUILDS) {
        // Rebuilding is not working. Say so instead of flickering a black map at the user.
        setWebglError(webglUnavailableMessage);
        return;
      }
      setMapGeneration((g) => g + 1);
    };
    canvas.addEventListener("webglcontextlost", handleContextLost);

    // Belt and braces: a context can also come back already dead, with no event to listen for --
    // which is exactly the state this screen was found in. If the map has not finished loading by
    // the time it plainly should have, check the context and rebuild on the same path.
    const watchdog = window.setTimeout(() => {
      const map = mapRef.current;
      if (!map || map.loaded()) return;
      const lost = (map as any).painter?.context?.gl?.isContextLost?.();
      if (lost) handleContextLost(new Event("webglcontextlost"));
    }, MAP_LOAD_WATCHDOG_MS);

    mapRef.current.on("error", (ev: any) => {
      if (/webgl/i.test(ev?.error?.message ?? "")) setWebglError(webglUnavailableMessage);
    });
    mapRef.current.on("style.load", () => {
      onStyleLoad();
    });
    mapRef.current.on("load", () => {
      onLoad();
    });

    return () => {
      window.clearTimeout(watchdog);
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      cleanupMapSources();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      // Clear cache to prevent memory leaks
      setClientPointsCache(new Map());
    };
    // sourcePrefix so each instance gets its own map; mapGeneration so a lost context rebuilds it.
  }, [sourcePrefix, mapGeneration]);

  useEffect(() => {
    setSearch((prev) => ({ ...prev, timezone }));
  }, [timezone]);

  const buildStopsCollection = (results) => {
    let fc: FeatureCollection = {
      type: "FeatureCollection",
      features: results.map((r) => ({
        type: "Feature",
        properties: { ...r },
        geometry: { type: "Point", coordinates: [r.longitude, r.latitude] },
      })),
    };
    mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-layer`, "visibility", "visible");
    if (mapRef.current.getLayer(`${sourcePrefix}-stops-clusters`)) {
      mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-clusters`, "visibility", "visible");
    }
    return fc;
  };

  const buildLinesCollection = (results, skipFitBounds = false) => {
    var coordinates = results.map((r) => [r.longitude, r.latitude]);

    if (coordinates?.length < 2) {
      mapRef.current.setLayoutProperty(`${sourcePrefix}-lines-layer`, "visibility", "none");
      mapRef.current.setLayoutProperty(`${sourcePrefix}-headings-layer`, "visibility", "none");
      setLayerToggle({
        ...layerToggle,
        stops: true,
        headings: false,
        headingsEnabled: false,
        lines: false,
        linesEnabled: false,
      });
      if (coordinates?.length == 1) {
        mapRef.current.setCenter({ lng: coordinates[0][0], lat: coordinates[0][1] });
      }
      return;
    }

    mapRef.current.setLayoutProperty(`${sourcePrefix}-lines-layer`, "visibility", "visible");

    let result: FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: coordinates,
          },
        },
      ],
    };

    if (coordinates?.length > 0 && !skipFitBounds) {
      const points = coordinates;

      const line = lineString(points);
      const [minLng, minLat, maxLng, maxLat] = bbox(line);
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 1000 }
      );
    }
    return result;
  };

  const buildHeadingsCollection = (results) => {
    let fc: FeatureCollection = {
      type: "FeatureCollection",
      features: results.map((r) => ({
        type: "Feature",
        properties: { ...r },
        geometry: { type: "Point", coordinates: [r.longitude, r.latitude] },
      })),
    };
    mapRef.current.setLayoutProperty(`${sourcePrefix}-headings-layer`, "visibility", "visible");
    return fc;
  };

  const buildEventsCollection = (results) => {
    if (!results) return;
    let filtered = results.filter((r) => r.location);
    let fc: FeatureCollection = {
      type: "FeatureCollection",
      features: filtered?.map((r) => ({
        type: "Feature",
        properties: { ...r },
        geometry: { type: "Point", coordinates: [r.location?.longitude, r.location?.latitude] },
      })),
    };
    mapRef.current.setLayoutProperty(`${sourcePrefix}-trip-events-layer`, "visibility", "visible");
    return fc;
  };

  const buildPointsCollection = (results) => {
    if (!results) return;
    let fc: FeatureCollection = {
      type: "FeatureCollection",
      features: results?.map((r) => ({
        type: "Feature",
        properties: { ...r },
        geometry: { type: "Point", coordinates: [r?.longitude, r?.latitude] },
      })),
    };
    mapRef.current.setLayoutProperty(`${sourcePrefix}-points-layer`, "visibility", "visible");

    mapRef.current.getSource(`${sourcePrefix}-client-points`)["setData"](fc);
    return fc;
  };

  const setLayers = (telemetry, stops, headings, events, skipFitBounds = false) => {
    let headingsCollection = buildHeadingsCollection(headings);
    if (headingsCollection) {
      mapRef.current.getSource(`${sourcePrefix}-headings`)["setData"](headingsCollection);
    }

    let linesCollection = buildLinesCollection(telemetry, skipFitBounds);
    if (linesCollection) {
      mapRef.current.getSource(`${sourcePrefix}-vehicles`)["setData"](linesCollection);
    }

    if (props.numberedMarkers && props.numberedMarkers.length) {
      // GPS Reconstruction tab: the numbered recon markers stand in for the raw GPS stop markers so
      // there's a single set of numbers that matches the cards. Clear + hide the raw stops layer.
      mapRef.current.getSource(`${sourcePrefix}-stops`)["setData"]({ type: "FeatureCollection", features: [] });
      if (mapRef.current.getLayer(`${sourcePrefix}-stops-layer`)) {
        mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-layer`, "visibility", "none");
      }
      if (mapRef.current.getLayer(`${sourcePrefix}-stops-clusters`)) {
        mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-clusters`, "visibility", "none");
      }
    } else {
      let stopsCollection = buildStopsCollection(stops);
      if (stopsCollection) {
        mapRef.current.getSource(`${sourcePrefix}-stops`)["setData"](stopsCollection);
      }
    }

    if (props.hideDriverEvents) {
      // Keep the driver's pins off this map entirely — clear the source and hide the layer.
      mapRef.current.getSource(`${sourcePrefix}-trip-events`)["setData"]({ type: "FeatureCollection", features: [] });
      if (mapRef.current.getLayer(`${sourcePrefix}-trip-events-layer`)) {
        mapRef.current.setLayoutProperty(`${sourcePrefix}-trip-events-layer`, "visibility", "none");
      }
    } else {
      let eventsCollection = buildEventsCollection(events);
      if (eventsCollection) {
        mapRef.current.getSource(`${sourcePrefix}-trip-events`)["setData"](eventsCollection);
      }
    }
     buildPointsCollection(clientPoints);
  };

  const searchHistory = (searchRequest) => {
    // Cancel any in-flight summary request
    summaryCancelRef.current?.cancel();
    const source = axios.CancelToken.source();
    summaryCancelRef.current = source;

    vehiclesService.getVehicleActivitySummary(searchRequest, source.token).then((result) => {
      if (result) setSummary(result);
    });
    dispatch(getVehicleTripData(searchRequest));
  };

  useEffect(() => {
    if (_allData === undefined || !mapLoaded) return;
    if (!_allData || _allData.telemetry?.length < 1) {
      mapRef.current.setLayoutProperty(`${sourcePrefix}-lines-layer`, "visibility", "none");
      mapRef.current.setLayoutProperty(`${sourcePrefix}-headings-layer`, "visibility", "none");
      mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-layer`, "visibility", "none");
      if (mapRef.current.getLayer(`${sourcePrefix}-stops-clusters`)) {
        mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-clusters`, "visibility", "none");
      }
    } else {
      setLayerToggle({
        ...layerToggle,
        stops: true,
        headings: true,
        lines: true,
        headingsEnabled: true,
        linesEnabled: true,
      });
    }
    
    if (_allData) {
      setLayers(_allData.telemetry || [], _allData.stops || [], _allData.headings || [], orderedTimeline);
      setActivity(_allData.activity?.map((s, i) => ({ ...s, index: i + 1, id: i })));
    }
  }, [_allData, mapLoaded]);

  // Pull together telemetry points sorted by time. The Play animation walks these
  // in order, interpolating between consecutive points.
  const getPlayPath = (): Array<{ lng: number; lat: number; heading?: number }> => {
    const tele = _allData?.telemetry || [];
    return tele
      .filter((t: any) => t?.latitude != null && t?.longitude != null && t?.occurrenceDT)
      .slice()
      .sort((a: any, b: any) => new Date(a.occurrenceDT).getTime() - new Date(b.occurrenceDT).getTime())
      .map((t: any) => ({ lng: t.longitude, lat: t.latitude, heading: t.heading }));
  };

  const stopPlay = () => {
    if (playRafRef.current != null) {
      cancelAnimationFrame(playRafRef.current);
      playRafRef.current = null;
    }
    if (playMarkerRef.current) {
      playMarkerRef.current.remove();
      playMarkerRef.current = null;
    }
    playInnerRef.current = null;
    playPathRef.current = [];
    playProgressRef.current = 0;
    setIsPlaying(false);
    setIsPaused(false);
  };

  // Base drive time at 1× speed. Slower than the original 8s so the van is easier
  // to follow; the speed control and pause let dispatchers slow it further or hold.
  const PLAY_BASE_MS = 20000;

  // Advance the van by the time elapsed since the last frame, scaled by playSpeed.
  // Reads progress/path/speed from refs so pause/resume and speed changes don't
  // require tearing down and rebuilding the loop.
  const tick = (now: number) => {
    const path = playPathRef.current;
    const inner = playInnerRef.current;
    if (!playMarkerRef.current || path.length < 2) return;

    const dt = now - playLastFrameRef.current;
    playLastFrameRef.current = now;
    playProgressRef.current = Math.min(
      1,
      playProgressRef.current + (dt / PLAY_BASE_MS) * playSpeedRef.current
    );
    const t = playProgressRef.current;

    // Map progress to a fractional index in the path
    const idxF = t * (path.length - 1);
    const i0 = Math.floor(idxF);
    const i1 = Math.min(path.length - 1, i0 + 1);
    const frac = idxF - i0;
    const a = path[i0];
    const b = path[i1];
    const lng = a.lng + (b.lng - a.lng) * frac;
    const lat = a.lat + (b.lat - a.lat) * frac;
    // Heading from a lookahead bearing: aim ~5 points down the path so that near-
    // stationary segments (vehicle turning around, dwelling at a stop) don't make
    // the van flip 180° from tiny dx/dy noise in adjacent telemetry points.
    const lookahead = Math.min(path.length - 1, i0 + 5);
    const look = path[lookahead];
    const dy2 = look.lat - a.lat;
    const dx2 = look.lng - a.lng;
    const heading = (Math.abs(dy2) + Math.abs(dx2) < 1e-6)
      ? 0
      : (Math.atan2(dx2, dy2) * 180) / Math.PI; // 0 = north
    playMarkerRef.current?.setLngLat([lng, lat]);
    // 🚐 emoji's hood faces left in most fonts (~270°). +90° puts the van's front
    // in the direction of travel: heading 0 (north) → van points up.
    if (inner) inner.style.transform = `rotate(${heading + 90}deg)`;

    if (t < 1 && playMarkerRef.current) {
      playRafRef.current = requestAnimationFrame(tick);
    } else {
      // Finish flourish — wave and disappear
      if (inner) {
        inner.textContent = "🏁";
        inner.style.transform = "rotate(0deg)";
      }
      setTimeout(() => stopPlay(), 900);
    }
  };

  const startPlay = () => {
    if (!mapRef.current) return;
    const path = getPlayPath();
    if (path.length < 2) return; // nothing to drive
    stopPlay(); // belt-and-suspenders

    // Build a little 🚐 marker. Don't set `transform` on this root element —
    // Mapbox Marker uses transform on the root to position the marker, so writing
    // to it clobbers positioning. Rotate the inner span instead.
    const el = document.createElement("div");
    el.style.userSelect = "none";
    el.style.cursor = "default";
    el.style.filter = "drop-shadow(0 1px 1px rgba(0,0,0,.4))";
    const inner = document.createElement("span");
    inner.style.display = "inline-block";
    inner.style.fontSize = "28px";
    inner.style.lineHeight = "1";
    inner.style.transition = "transform 80ms linear";
    inner.style.transformOrigin = "center";
    inner.textContent = "🚐";
    el.appendChild(inner);

    const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
      .setLngLat([path[0].lng, path[0].lat])
      .addTo(mapRef.current);
    playMarkerRef.current = marker;
    playInnerRef.current = inner;
    playPathRef.current = path;
    playProgressRef.current = 0;
    playLastFrameRef.current = performance.now();
    setIsPlaying(true);
    setIsPaused(false);

    playRafRef.current = requestAnimationFrame(tick);
  };

  const pausePlay = () => {
    if (!isPlaying || isPaused) return;
    if (playRafRef.current != null) {
      cancelAnimationFrame(playRafRef.current);
      playRafRef.current = null;
    }
    setIsPaused(true);
  };

  const resumePlay = () => {
    if (!isPlaying || !isPaused || !playMarkerRef.current) return;
    // Reset the frame clock so the paused interval isn't counted as elapsed time.
    playLastFrameRef.current = performance.now();
    setIsPaused(false);
    playRafRef.current = requestAnimationFrame(tick);
  };

  // Single primary control: Drive → Pause → Resume → Pause → … There's no hard
  // stop; the van either drives, holds (paused), or finishes the route on its own.
  const togglePlay = () => {
    if (!isPlaying) startPlay();
    else if (isPaused) resumePlay();
    else pausePlay();
  };

  // Clean up any in-flight animation when the component unmounts or the trip changes.
  useEffect(() => () => stopPlay(), []);
  useEffect(() => { stopPlay(); }, [search.tripNumber, search.unitNumber]);

  // Pan and zoom (if needed) so the row's stop/event marker is actually visible.
  // Used for stops and timeline events — points on the map, not segments. Keeps
  // the current zoom if the user is already zoomed in past 15, otherwise pulls
  // them in to street-level so the marker isn't lost in a wide view.
  const panToRow = (row: any) => {
    if (!mapRef.current || !row) return;
    // Activity stops carry startLatitude/Longitude; timeline events carry location.
    const lat = row.startLatitude ?? row.location?.latitude;
    const lng = row.startLongitude ?? row.location?.longitude;
    if (lat == null || lng == null) return;
    const currentZoom = mapRef.current.getZoom();
    mapRef.current.easeTo({
      center: [lng, lat],
      zoom: Math.max(currentZoom, 15),
      duration: 600,
    });
  };

  // Feed the numbered reconstruction markers (GPS Reconstruction tab). Keyed on the prop + mapLoaded
  // so it repopulates after a regenerate or once the map/source is ready.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const src: any = mapRef.current.getSource(`${sourcePrefix}-recon-markers`);
    if (!src) return;
    const markers = props.numberedMarkers || [];
    src.setData({
      type: "FeatureCollection",
      features: markers
        .filter((m) => m.latitude != null && m.longitude != null)
        .map((m) => ({
          type: "Feature",
          properties: { label: String(m.label) },
          geometry: { type: "Point", coordinates: [m.longitude, m.latitude] },
        })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.numberedMarkers, mapLoaded]);

  // Fly to a point requested by the parent (a right-panel row click on the GPS Reconstruction tab).
  // Reuses the same pan/zoom behavior as panToRow — keep the current zoom if already past 15,
  // otherwise pull in to street level so the marker isn't lost in a wide view.
  useEffect(() => {
    const fp = props.focusPoint;
    if (!fp || !mapRef.current || !mapLoaded) return;
    if (fp.latitude == null || fp.longitude == null) return;
    const currentZoom = mapRef.current.getZoom();
    mapRef.current.easeTo({
      center: [fp.longitude, fp.latitude],
      zoom: Math.max(currentZoom, 15),
      duration: 700,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusPoint, mapLoaded]);

  // Toggle a row in/out of the highlight set. Replaces the old "set selectedRow →
  // filter+zoom" behavior with additive highlight overlay (no zoom, multi-select).
  const toggleHighlight = (row: any) => {
    if (!row) return;
    setHighlightedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  // Rebuild the highlight overlay when the user toggles a row in/out, or when the
  // underlying activity data refreshes. Default route stays untouched — overlay
  // layers add navy lines / grey chevrons just over the selected segments.
  //
  // We build one LineString feature PER highlighted range here instead of reusing
  // buildLinesCollection — that builder emits a single connected line through all
  // points and has visibility/state side effects on the default layer.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const emptyFC: any = { type: "FeatureCollection", features: [] };
    const linesSource: any = mapRef.current.getSource(`${sourcePrefix}-highlight-lines`);
    const headingsSource: any = mapRef.current.getSource(`${sourcePrefix}-highlight-headings`);
    if (!linesSource || !headingsSource) return;

    if (highlightedRowIds.size === 0 || !_allData?.telemetry?.length) {
      linesSource.setData(emptyFC);
      headingsSource.setData(emptyFC);
      return;
    }

    const ranges = (orderedActivity || [])
      .filter((r: any) => r?.startDateUTC && r?.endDateUTC && highlightedRowIds.has(r.id))
      .map((r: any) => [new Date(r.startDateUTC).getTime(), new Date(r.endDateUTC).getTime()] as [number, number]);
    if (ranges.length === 0) {
      linesSource.setData(emptyFC);
      headingsSource.setData(emptyFC);
      return;
    }

    // One LineString per range — keeps separate segments visually disconnected so
    // disjoint highlights don't draw a crow-flies line between them.
    const tele = _allData.telemetry || [];
    const lineFeatures: any[] = [];
    for (const [start, end] of ranges) {
      const coords = tele
        .filter((t: any) => {
          const ts = new Date(t.occurrenceDT).getTime();
          return ts >= start && ts <= end && t?.longitude != null && t?.latitude != null;
        })
        .map((t: any) => [t.longitude, t.latitude]);
      if (coords.length >= 2) {
        lineFeatures.push({
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: coords },
        });
      }
    }
    linesSource.setData({ type: "FeatureCollection", features: lineFeatures });

    // Headings can stay as a flat point collection — each is its own feature.
    const headingFeatures = (_allData.headings || [])
      .filter((h: any) => {
        const ts = new Date(h.occurrenceDT).getTime();
        return ranges.some(([s, e]) => ts >= s && ts <= e) && h?.longitude != null && h?.latitude != null;
      })
      .map((h: any) => ({
        type: "Feature",
        properties: { heading: h.heading ?? 0 },
        geometry: { type: "Point", coordinates: [h.longitude, h.latitude] },
      }));
    headingsSource.setData({ type: "FeatureCollection", features: headingFeatures });
  }, [highlightedRowIds, _allData, orderedActivity, mapLoaded]);

  useEffect(() => {
    mapRef.current?.resize();
  }, [props.isHidden]);

  useEffect(() => {
    setTimeout(() => mapRef.current?.resize(), 300); // After collapse animation
  }, [headerCollapsed]);

  useEffect(() => {
    const isReady = !!(orderedStops && _allData?.headings && _allData?.telemetry);
    props.onMapDataReady?.(isReady);
  }, [orderedStops, _allData]);

  const debounceSearch = useCallback(debounce(searchHistory, 700), []);
  
  useEffect(() => {
    if (!mapLoaded) return;
    if (!search.unitNumber) {
      // In trip mode, unitNumber gets set asynchronously - don't clear data while waiting
      if (!search.tripNumber) {
        dispatch(clearVehicleTripData());
        setActivityLoading(false);
      }
      return;
    }

    // In trip mode, don't fetch until timeline-derived dates are ready
    if (search.tripNumber && !tripDatesReady.current) return;

    // Every window change fetches that window (filterOnly included), so the map always shows exactly
    // the GPS for the dates on screen, whichever way the window moved. getVehicleTripData skips a
    // request identical to the one already loaded, and the loading flag drives the spinner meanwhile.
    debounceSearch({ ...search, clientName: trip?.clientName });

    if(!trip) return;

    // Check cache first for client points
    const clientPointsCacheKey = `${trip.vehicle?.unitNumber}-${trip.clientName}-${search.startDate.format()}-${search.endDate.format()}`;
    if (clientPointsCache.has(clientPointsCacheKey)) {
      setClientPoints(clientPointsCache.get(clientPointsCacheKey));
      return;
    }

    vehiclesService
        .getVehicleClientPoints(
          trip?.vehicle?.unitNumber,
          trip?.clientName,
          search.startDate.format(),
          search.endDate.format()
        )
        .then((results) => {
          setClientPoints(results);
          // Cache the results
          setClientPointsCache(prev => new Map(prev.set(clientPointsCacheKey, results)));
        });
  }, [search, mapLoaded]);

  useEffect(() => {
    if (!search.tripNumber) return;
    setSelectedRow(undefined);
    setActivity(undefined);
    setActivityLoading(true);
    setDataInitialized(false);

    dispatch(getTrip(search.tripNumber));
    dispatch(getTimeline({ tripNumber: search.tripNumber, isActive: "active" }));
  }, [search.tripNumber]);

  // Track when both activity and timeline loading are complete
  useEffect(() => {
    if (!activityLoading && !timelineLoading) {
      setDataInitialized(true);
    }
  }, [activityLoading, timelineLoading]);

  useEffect(() => {
    if (!trip) return;
    setSearch((prev) => ({
      ...prev,
      unitNumber: +trip.vehicle?.unitNumber,
    }));

  }, [trip]);

  useEffect(() => {
    if (!props.tripNumber) return;
    setSearch((prev) => ({ ...prev, tripNumber: props.tripNumber }));
  }, [props.tripNumber]);

  // Handle external event selection (from CloserEventsGrid or URL param)
  const urlEventId = searchParams.get("eventId") ? Number(searchParams.get("eventId")) : null;
  const effectiveEventId = props.selectedEventId ?? urlEventId;
  const initialEventApplied = useRef(false);
  useEffect(() => {
    if (effectiveEventId == null || !orderedActivity?.length) return;
    // Only auto-select on initial load, not on every filter change
    if (initialEventApplied.current && !props.selectedEventId) return;

    const index = orderedActivity.findIndex((item: any) => item.eventId === effectiveEventId);
    if (index >= 0) {
      setSelectedRow(orderedActivity[index]);
      setSelectedIndex(index);
      initialEventApplied.current = true;

      // Scroll the row into view
      setTimeout(() => {
        const container = document.getElementById(`activity-cards-container-${sourcePrefix}`);
        if (container) {
          const rowElement = container.querySelector(`[data-activity-index="${index}"]`);
          rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [effectiveEventId, orderedActivity, sourcePrefix]);

  useEffect(() => {
    if (!mapLoaded || !_tripTimeline) return;

    let r = _tripTimeline.map((s, i) => ({ ...s, index: i + 1, id: i }));
    setOrderedTimeline(r);

    if (_allData) {
      setLayers(_allData.telemetry || [], _allData.stops || [], _allData.headings || [], r);
    } else {
      setLayers([], [], [], r);
      // Only setCenter as fallback when no telemetry data (fitBounds in buildLinesCollection handles zoom otherwise)
      if (_tripTimeline?.length >= 1 && _tripTimeline[0].location) {
        mapRef.current.setCenter({
          lng: _tripTimeline[0].location?.longitude,
          lat: _tripTimeline[0].location?.latitude,
        });
      }
    }

    let start = _tripTimeline.find((t) => t.type == "Begin Trip")?.time;
    let end = _tripTimeline.find((t) => t.type === "End Trip")?.time;
    if (!end) end = moment.utc(start).add(12, "hours").format();
    tripDatesReady.current = true;
    tripStartDate.current = start;
    tripEndDate.current = end;
    setSearch((prev) => ({
      ...prev,
      startDate: moment.utc(start),
      endDate: moment.utc(end),
    }));
  }, [_tripTimeline, mapLoaded]);

  const onShowAllActivityClick = () => {
    setLayerToggle({
      ...layerToggle,
      stops: true,
      headings: true,
      lines: true,
      headingsEnabled: true,
      linesEnabled: true,
    });
    setLayers(_allData.telemetry, orderedStops, _allData.headings, orderedTimeline);
    setSelectedRow(undefined);

    // Reset dates back to trip dates
    if (_tripTimeline) {
      let start = _tripTimeline.find((t) => t.type === "Begin Trip")?.time;
      let end = _tripTimeline.find((t) => t.type === "End Trip")?.time;
      if (start) {
        if (!end) end = moment.utc(start).add(12, "hours").format();
        setSearch((prev) => ({
          ...prev,
          startDate: moment.utc(start),
          endDate: moment.utc(end),
        }));
      }
    }
  };

  const onLayerToggleChange = (e) => {
    // Belt and braces behind hiding the control: a toggle that arrives while the style is being
    // swapped, or after the map has gone, must not throw -- this runs from an event handler, so
    // an exception here takes the page down through the error boundary rather than failing the
    // one action.
    if (!mapRef.current || !mapRef.current.getStyle || !mapRef.current.getStyle()) return;
    if (e.target.id === "satellite") {
      // diff:false is deliberate. setStyle defaults to diffing the old style into the new one,
      // and this particular hop cannot be diffed -- mapbox logs "Unable to perform style diff:
      // Unimplemented: setSprite. Rebuilding the style from scratch." and falls back to a rebuild
      // that leaves the map with a style but no working sources: it stops requesting tiles
      // entirely and paints the new style's background colour, which on satellite is near-black.
      // Asking for the clean reload up front skips the broken half-way state.
      const nextStyle =
        mapRef.current.getStyle().name === "Mapbox Satellite Streets"
          ? "mapbox://styles/mapbox/streets-v11"
          : "mapbox://styles/mapbox/satellite-streets-v12";
      // Cast: @types/mapbox-gl is still on 2.x here and declares the other setStyle options as
      // required. Derived from the method's own signature rather than naming a type, because 2.x
      // and mapbox-gl 3.x's bundled types disagree on the name. Dropping the stale @types package
      // is a separate job.
      mapRef.current.setStyle(nextStyle, { diff: false } as Parameters<mapboxgl.Map["setStyle"]>[1]);
    } else {
     mapRef.current.setLayoutProperty(`${sourcePrefix}-${e.target.id}-layer`, "visibility", e.target.checked ? "visible" : "none");
     // Also toggle cluster layers when stops toggle changes
     if (e.target.id === "stops") {
       const vis = e.target.checked ? "visible" : "none";
       if (mapRef.current.getLayer(`${sourcePrefix}-stops-clusters`)) {
         mapRef.current.setLayoutProperty(`${sourcePrefix}-stops-clusters`, "visibility", vis);
       }
     }
   }
    layerToggle[e.target.id] = e.target.checked;
    setLayerToggle({ ...layerToggle });
  };

  useEffect(() => {
    // Prefer the parent's staged events list (in-flight edits / drags / removes)
    // over the Redux server snapshot, so reorders show up without a save round trip.
    const sourceTimeline = props.tripEventsOverride ?? _tripTimeline;

    // For unit number searches, we only need activity. For trip searches, we need both.
    if (activity == undefined) return;

    // If searching by trip number, wait for timeline. Otherwise, process activity alone.
    if (search.tripNumber && sourceTimeline == undefined) return;

    if (!activity?.length && !sourceTimeline?.length) {
      setActivityLoading(false);
      return;
    }

    // Combine activity and timeline (if timeline exists)
    // Filter by search date range so time filter works
    let filteredTimeline = sourceTimeline;
    let filteredActivity = activity;
    if (search.startDate && search.endDate) {
      const start = search.startDate.valueOf();
      const end = search.endDate.valueOf();
      if (sourceTimeline) {
        filteredTimeline = sourceTimeline.filter((t) => {
          if (!t.time) return true;
          const eventTime = moment.utc(t.time).valueOf();
          return eventTime >= start && eventTime <= end;
        });
      }
      if (activity) {
        filteredActivity = activity.filter((a) => {
          const actStart = moment.utc(a.startDateUTC).valueOf();
          const actEnd = moment.utc(a.endDateUTC).valueOf();
          return actEnd >= start && actStart <= end;
        });
      }
    }
    let r = filteredTimeline ? filteredActivity?.concat(filteredTimeline) : filteredActivity;

    if (!r?.length) return;

    r.sort((a, b) => {
      let first = moment.utc(Object.hasOwn(a, "activityId") ? a.startDateUTC : (a.time?.replace("Z", "") ?? ""));
      let second = moment.utc(Object.hasOwn(b, "activityId") ? b.startDateUTC : (b.time?.replace("Z", "") ?? ""));
      if (first < second) return -1;
      else if (first > second) return 1;
      else return 0;
    });

    // Number only timeline events (blue); activity items (stops/movements) get no index
    // so that event numbers match the Entry column in the Closer grid
    let eventIndex = 0;
    let ordered = r.map((s, i) => {
      const isEvent = !Object.hasOwn(s, "activityId");
      return { ...s, index: isEvent ? ++eventIndex : "", id: i };
    });

    if (ordered?.length) {
      let stops = [];
      if (_allData?.stops?.length) {
        stops = _allData.stops
          .slice()
          .sort((a, b) => (a.occurrenceDT < b.occurrenceDT ? -1 : 1))
          .map((s, i) => ({ ...s, index: i + 1 }));

        // Filter stops by date range. Include stops that STARTED before the trip
        // window if they were still ongoing when the trip began (e.g. overnight
        // parking) — judged by occurrenceDT + duration. Without this, an overnight
        // stop disappears entirely just because its occurrence time is yesterday.
        if (search.startDate && search.endDate) {
          const start = search.startDate.valueOf();
          const end = search.endDate.valueOf();
          stops = stops.filter((s) => {
            const stopStart = moment.utc(s.occurrenceDT).valueOf();
            const durMs = ((s.duration ?? 0) as number) * 60000; // duration in minutes
            const stopEnd = stopStart + durMs;
            return stopStart <= end && stopEnd >= start;
          });
        }
        setOrderedStops(stops);
      }
      let tl = ordered.filter((o) => Object.hasOwn(o, "time"));

      setOrderedTimeline(tl);
      if (_allData) {
        let telemetry = _allData.telemetry || [];
        let headings = _allData.headings || [];

        // Filter map data by date range
        if (search.startDate && search.endDate) {
          const start = search.startDate.valueOf();
          const end = search.endDate.valueOf();
          telemetry = telemetry.filter((t) => moment.utc(t.occurrenceDT).valueOf() >= start && moment.utc(t.occurrenceDT).valueOf() <= end);
          headings = headings.filter((h) => moment.utc(h.occurrenceDT).valueOf() >= start && moment.utc(h.occurrenceDT).valueOf() <= end);
        }

        setLayers(telemetry, stops, headings, tl);
      }
    }

    setOrderedActivity(ordered);
    setSelectedIndex(undefined);
    setActivityLoading(false);
  }, [activity, _tripTimeline, props.tripEventsOverride, search.startDate?.valueOf(), search.endDate?.valueOf()]);

  useEffect(()=> {
    if(!_allData || !mapLoaded || _mapStyleReloadedCount === 0) return;

    // Clean up existing sources before reloading.
    //
    // setLayers waits for the icons: a full style reload wipes every registered image, and symbol
    // layers built before their icon exists draw nothing at all.
    cleanupMapSources();
    loadImagesAndSources().then(() => {
      if (!mapRef.current) return;
      setLayers(_allData.telemetry || [], _allData.stops || [], _allData.headings || [], orderedTimeline);
    });

  },[_mapStyleReloadedCount])

  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setMapWidthPercent(Math.min(Math.max(percent, 20), 80));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      mapRef.current?.resize();
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const mapView = () => {
    return (
      <>
        {/* Hover popups are dark (vh-popup-dark). When the user clicks a marker we
            add vh-popup-pinned so the pinned-view flips to light. CSS specificity
            puts -pinned overrides after -dark so the light style wins. */}
        <style>{`
          .vh-popup-dark .mapboxgl-popup-content {
            background: #111;
            color: #fff;
            padding: 8px 10px;
            font-size: 12px;
            line-height: 1.4;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,.4);
          }
          .vh-popup-dark .mapboxgl-popup-tip { border-top-color: #111; border-bottom-color: #111; }
          .vh-popup-dark .mapboxgl-popup-close-button { color: #fff; font-size: 18px; padding: 0 6px; }

          .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-content {
            background: #fff;
            color: #111;
            box-shadow: 0 2px 8px rgba(0,0,0,.25);
          }
          .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-tip { border-top-color: #fff; border-bottom-color: #fff; }
          .vh-popup-dark.vh-popup-pinned .mapboxgl-popup-close-button { color: #111; }
        `}</style>
        <Grid container sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "70vh", flexWrap: "nowrap" }}>
          {!props.hideFilters && (
            <>
              <Grid item xs="auto" sx={{ flexShrink: 0, display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => setHeaderCollapsed((prev) => !prev)}>
                <IconButton size="small">
                  {headerCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  {headerCollapsed ? "Show filters" : "Hide filters"}
                </Typography>
              </Grid>
              <Collapse in={!headerCollapsed} sx={{ flexShrink: 0 }}>
                <VehicleHistoryFilters
                  search={search}
                  setSearch={setSearch}
                  layerToggle={layerToggle}
                  onLayerToggleChange={onLayerToggleChange}
                  selectedRow={selectedRow}
                  onShowAllActivityClick={onShowAllActivityClick}
                  datesModified={!!(tripStartDate.current && search.startDate && search.endDate &&
                    (search.startDate.valueOf() !== moment.utc(tripStartDate.current).valueOf() ||
                     search.endDate.valueOf() !== moment.utc(tripEndDate.current).valueOf()))}
                  mapReady={mapLoaded && !activityLoading && !timelineLoading}
                />
              </Collapse>
            </>
          )}
          <Grid item xs="auto" sx={{ flexShrink: 0 }}>
            <ActivitySummary summary={summary} orderedTimeline={orderedTimeline} dataInitialized={dataInitialized} />
          </Grid>
          <Grid item xs sx={{ flex: 1, minHeight: 0 }}>
            <Box ref={containerRef} sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, height: "100%" }}>
              <Box sx={{ width: { xs: "100%", md: `${mapWidthPercent}%` }, height: { xs: "55vh", md: "100%" }, minWidth: 0, position: "relative" }}>
                <Box ref={mapContainer} className="map-container" id={`map-container-${sourcePrefix}`} sx={{ height: "100%" }} />
                {/* Drive-the-route playback controls. Sends a little 🚐 along the
                    telemetry so dispatchers can watch the trip without paging through
                    stops. Primary button cycles Drive → Pause → Resume; plus a live
                    speed selector. */}
                {(_allData?.telemetry?.length || 0) > 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      zIndex: 5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Box
                      onClick={togglePlay}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: "20px",
                        backgroundColor: isPlaying && !isPaused ? "#1565c0" : "#1a237e",
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                        boxShadow: "0 1px 4px rgba(0,0,0,.3)",
                        userSelect: "none",
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      {!isPlaying ? "🚐 Drive" : isPaused ? "▶️ Resume" : "⏸️ Pause"}
                    </Box>
                    {/* Speed selector — cycles 0.5× → 1× → 2× → 0.5×. Applies live. */}
                    <Box
                      onClick={() => setPlaySpeed((s) => (s === 0.5 ? 1 : s === 1 ? 2 : 0.5))}
                      title="Playback speed"
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: "20px",
                        backgroundColor: "#37474f",
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                        boxShadow: "0 1px 4px rgba(0,0,0,.3)",
                        userSelect: "none",
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      {playSpeed}×
                    </Box>
                  </Box>
                )}
                {webglError && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                      backgroundColor: "rgba(255,255,255,0.95)",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="error">{webglError}</Typography>
                  </Box>
                )}
              </Box>
              <Box
                onMouseDown={onDividerMouseDown}
                sx={{
                  display: { xs: "none", md: "block" },
                  width: "6px",
                  cursor: "col-resize",
                  backgroundColor: "#ccc",
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "#999" },
                }}
              />
              <Box sx={{ width: { xs: "100%", md: `${100 - mapWidthPercent}%` }, height: { xs: "auto", md: "100%" }, minWidth: 0, overflow: "auto" }}>
                {props.rightPanelContent ? props.rightPanelContent : (
                  <VehicleActivityCards
                    search={search}
                    setSearch={setSearch}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    timezone={timezone}
                    orderedActivity={orderedActivity}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    activityLoading={activityLoading || timelineLoading || vehicleTripLoading}
                    dataInitialized={dataInitialized}
                    sourceIdPrefix={sourcePrefix}
                    editable={props.editable}
                    eventTypes={props.eventTypes}
                    waitReasons={props.waitReasons}
                    pointsOfService={props.pointsOfService}
                    onEventSave={props.onEventSave}
                    onEventRemove={props.onEventRemove}
                    onEventsReorder={props.onEventsReorder}
                    highlightedRowIds={highlightedRowIds}
                    onToggleHighlight={toggleHighlight}
                    onPanToRow={panToRow}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  return mapView();
};

export default VehicleHistory;
